import React, { useState } from 'react';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { Card } from '../../CommonComponents/OPL/Card/Card';
import { Icon } from '../../CommonComponents/OPL/Icon/Icon';
import { Modal } from '../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../CommonComponents/OPL/Modal/ModalHeader';
import { Stat } from '../../CommonComponents/OPL/Stat/Stat';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { BatchItemResult } from '../api/pdfBatchTypes';
import { StyledMarkdown } from './ItemInfoModal.style';
import {
  Heading1,
  Heading2,
} from '../../ApexOPL/AccountOpening/fields/ESigned/components/Disclosures/Disclosures.styles';

interface Props {
  item: BatchItemResult;
  onClose(): void;
}

export const ItemInfoModal = ({ item, onClose }: Props) => {
  const issueCount = item.warnings.length + item.errors.length;
  const [warningsOpen, setWarningsOpen] = useState(false);

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      size="large"
      aria={{ labelledby: 'item-info-modal-heading' }}
    >
      <ModalHeader id="item-info-modal-heading">{item.fileName}</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        {item.summary && (
          <Card p="16" mb="16" hasBorder>
            <Text fontSize="h5" fontWeight={600} mb="8">
              AI Summary
            </Text>
            <StyledMarkdown
              options={{
                overrides: {
                  h1: { component: Heading1 },
                  h2: { component: Heading2 },
                },
              }}
            >
              {item.summary}
            </StyledMarkdown>
          </Card>
        )}

        <Card p="16" hasBorder>
          <Text fontSize="h5" fontWeight={600} mb="12">
            Extracted Data
          </Text>

          <Box display="flex" mb="16" css={({ theme }) => ({ gap: theme.space['24'] })}>
            {item.principalName && (
              <Box>
                <Text variant="smallCaps" fontSize="h6" mb="2">
                  Principal
                </Text>
                <Text fontWeight={500}>{item.principalName}</Text>
              </Box>
            )}
            {item.spouseName && (
              <Box>
                <Text variant="smallCaps" fontSize="h6" mb="2">
                  Spouse
                </Text>
                <Text fontWeight={500}>{item.spouseName}</Text>
              </Box>
            )}
            {item.dependentCount > 0 && (
              <Box>
                <Text variant="smallCaps" fontSize="h6" mb="2">
                  Dependents
                </Text>
                <Text fontWeight={500}>{item.dependentCount.toString()}</Text>
              </Box>
            )}
            {item.trustCount > 0 && (
              <Box>
                <Text variant="smallCaps" fontSize="h6" mb="2">
                  Trusts
                </Text>
                <Text fontWeight={500}>{item.trustCount.toString()}</Text>
              </Box>
            )}
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            css={({ theme }) => ({ gap: theme.space['16'] })}
          >
            <Stat size="small" label="Accounts" value={item.accountCount.toString()} />
            <Stat size="small" label="Goals" value={item.goalCount.toString()} />
            <Stat size="small" label="Incomes" value={item.incomeCount.toString()} />
            <Stat size="small" label="Expenses" value={item.expenseCount.toString()} />
            <Stat size="small" label="Other Assets" value={item.otherAssetCount.toString()} />
            <Stat size="small" label="Insurances" value={item.insuranceCount.toString()} />
            {item.trustCount > 0 && (
              <Stat size="small" label="Trusts" value={item.trustCount.toString()} />
            )}
            {item.riskToleranceScore != null && (
              <Stat
                size="small"
                label="Risk Tolerance"
                value={item.riskToleranceScore.toString()}
              />
            )}
          </Box>
        </Card>

        {issueCount > 0 && (
          <Box mt="16">
            <Box
              display="flex"
              alignItems="center"
              css={({ theme }) => ({
                gap: theme.space[8],
                cursor: 'pointer',
                userSelect: 'none' as const,
              })}
              onClick={() => setWarningsOpen(prev => !prev)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setWarningsOpen(prev => !prev);
                }
              }}
            >
              <Icon
                className={`fas fa-chevron-${warningsOpen ? 'down' : 'right'}`}
                fontSize="small"
                color="neutral.500"
              />
              <Icon className="fas fa-exclamation-triangle" color="warning.500" fontSize="small" />
              <Text fontSize="small" fontWeight={500}>
                {issueCount} warning{issueCount !== 1 ? 's' : ''}
              </Text>
            </Box>

            {warningsOpen && (
              <Box mt="8" pl="24">
                {item.errors.map((error, i) => (
                  <Box
                    key={`e-${i}`}
                    display="flex"
                    mb="8"
                    css={({ theme }) => ({ gap: theme.space[8] })}
                  >
                    <Text color="danger.500" fontSize="small">
                      <Icon className="fas fa-times-circle" />
                    </Text>
                    <Text fontSize="small" color="danger.600">
                      {error}
                    </Text>
                  </Box>
                ))}

                {item.warnings.map((warning, i) => (
                  <Box
                    key={`w-${i}`}
                    display="flex"
                    mb="8"
                    css={({ theme }) => ({ gap: theme.space[8] })}
                  >
                    <Text color="warning.500" fontSize="small">
                      <Icon className="fas fa-exclamation-triangle" />
                    </Text>
                    <Text fontSize="small">{warning}</Text>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </ModalBody>

      <ModalFooter>
        {item.householdId && (
          <Button
            variant="subtleInactive"
            onClick={() => ADV.util.Router.go2('ConsumerOverview', { clientId: item.householdId })}
          >
            View Household
          </Button>
        )}
        <Button onClick={onClose} ml="12">
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
